import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';

export default function EditFAQModal({ open, onClose, faq, onSuccess }) {
  const [submitting, setSubmitting] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      question: '',
      answer: '',
      category: '',
      keywords: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (faq) {
      reset({
        question: faq.question || '',
        answer: faq.answer || '',
        category: faq.category || '',
        keywords: Array.isArray(faq.keywords) ? faq.keywords.join(',') : (faq.keywords || ''),
      });
    }
  }, [faq, reset]);

  const handleClose = () => {
    if (!submitting) {
      reset();
      onClose();
    }
  };

  const onSubmit = async (data) => {
    setSubmitting(true);

    try {
      await api.admin.updateFAQ(faq.id, data);
      toast.success('FAQ updated successfully');
      handleClose();
      onSuccess({ ...faq, ...data });
    } catch (error) {
      console.error('Failed to update FAQ:', error);
      toast.error(error.message || 'Failed to update FAQ');
    } finally {
      setSubmitting(false);
    }
  };

  if (!faq) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={submitting}
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>Edit FAQ</span>
          <IconButton onClick={handleClose} disabled={submitting} size="small">
            <X className="w-5 h-5" />
          </IconButton>
        </div>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <div className="space-y-4">
            {/* Question */}
            <Controller
              name="question"
              control={control}
              rules={{ required: 'Question is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Question *"
                  placeholder="Enter the FAQ question"
                  fullWidth
                  multiline
                  rows={2}
                  disabled={submitting}
                  error={!!errors.question}
                  helperText={errors.question?.message}
                />
              )}
            />

            {/* Answer */}
            <Controller
              name="answer"
              control={control}
              rules={{ required: 'Answer is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Answer *"
                  placeholder="Enter the FAQ answer"
                  fullWidth
                  multiline
                  rows={4}
                  disabled={submitting}
                  error={!!errors.answer}
                  helperText={errors.answer?.message}
                />
              )}
            />

            {/* Category */}
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Category (optional)"
                  placeholder="e.g., General, Technical, Billing"
                  fullWidth
                  disabled={submitting}
                />
              )}
            />

            {/* Keywords */}
            <Controller
              name="keywords"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Keywords (optional)"
                  placeholder="Comma-separated keywords for search optimization"
                  fullWidth
                  disabled={submitting}
                  helperText="Example: hours,office,schedule,time"
                />
              )}
            />
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Updating...' : 'Update FAQ'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

